import React from 'react';
import type { PiV2Appointment, PiV2Opportunity } from '../../../patient-insurance/typesV2';
import {
  piV2OpportunityMoney,
  piV2RelativeDay,
  piV2SortOpportunities,
  piV2TierBadge,
} from '../../../patient-insurance/utilsV2';
import { Icon } from '../../Icon/Icon';
import { Pill } from '../../Pill/Pill';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientInsuranceOpportunitiesProps {
  opportunities: PiV2Opportunity[];
  /** The visit these are for. Opportunities are never shown unattached. */
  appointment: PiV2Appointment;
}

/**
 * Ranked opportunities for one appointment, from `opportunity_window`.
 *
 * The title names the visit. These are lines to say to a person who is about
 * to be in the chair — detached from a date they are a to-do list nobody owns,
 * and the money attached to them is quoted against benefits that can still
 * change before a visit five weeks out.
 *
 * Ranked, not fixed-slot: fixed slots are a directory-card decision (scanning
 * 18 cards, a block that reorders per patient cannot be learned). On one
 * patient's page the top row should be the row worth raising.
 *
 * Every row carries money — an opportunity without a number is a nag. Only
 * actionable rows carry a script, and the script never states a price as a
 * guarantee: "your plan covers", "we estimate", never "you will pay".
 */
export function PatientInsuranceOpportunities({
  opportunities,
  appointment,
}: PatientInsuranceOpportunitiesProps) {
  const rows = piV2SortOpportunities(opportunities);

  return (
    <PatientProfileSectionCard
      icon="bolt"
      title={`Opportunities for ${appointment.whenLabel}`}
      accent="var(--ai-3)"
      subtitle={`${appointment.procedureLabel} · ${appointment.provider} · ${piV2RelativeDay(appointment.at)}`}
    >
      {rows.length === 0 ? (
        <p className="m-0 text-[13px] text-[var(--ink-3)]">
          Nothing to raise at this visit.
        </p>
      ) : (
        <div className="flex flex-col">
          {rows.map((o, i) => {
            const badge = piV2TierBadge(o.tier);
            return (
              <div
                key={o.id}
                className="flex gap-3 py-3.5"
                style={{ borderTop: i > 0 ? '1px solid var(--cream-2)' : 'none' }}
              >
                <span
                  className="mt-px flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg"
                  style={{ background: 'var(--ai-tint)', color: 'var(--ai-3)' }}
                >
                  <Icon kind={o.icon} size={13} stroke={1.8} />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13.5px] font-semibold text-[var(--ink-0)]">{o.title}</span>
                    <Pill tone={badge.tone}>{badge.label}</Pill>
                  </div>
                  <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">{o.detail}</div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="text-[10.5px] leading-tight text-[var(--ink-4)]">{o.moneyLabel}</div>
                  <div className="mt-0.5 font-[var(--font-mono)] text-[16px] font-semibold text-[var(--success)]">
                    {piV2OpportunityMoney(o)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PatientProfileSectionCard>
  );
}
